<script setup>
import { ref, computed } from 'vue'
import { useLocaleStore } from '@/stores/localeStore'
import otherProjectsData from '@/data/other-projects-data.json'

const localeStore = useLocaleStore()
const currentLocale = computed(() => localeStore.currentLocale)

const projects = computed(() => {
  return otherProjectsData[currentLocale.value]?.projects || []
})

const selectedProject = ref(null)

const selectProject = (project) => {
  selectedProject.value = project
}

const goBack = () => {
  selectedProject.value = null
}
</script>

<template>
  <div class="relative right-0 h-content-window overflow-hidden">
    <div class="w-full h-full bg-white overflow-auto">
      <!-- Vue détaillée du projet -->
      <div v-if="selectedProject" class="bg-gradient-to-br from-gray-50 to-white min-h-full">
        <div class="max-w-5xl mx-auto p-6 md:p-8">
          <!-- Header avec bouton retour -->
          <div class="flex items-center gap-3 mb-8">
            <button
              @click="goBack"
              class="flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white rounded-lg hover:bg-gray-50 hover:border-blue-400 hover:text-blue-600 transition-all duration-200 shadow-sm cursor-pointer group"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                class="group-hover:-translate-x-1 transition-transform duration-200"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              <span class="text-sm font-trebuchet-pixel font-medium">{{ $t('common.back') }}</span>
            </button>
          </div>

          <!-- Image du projet avec ombre élégante -->
          <div v-if="selectedProject.image" class="mb-8 flex justify-center">
            <div class="relative rounded-xl overflow-hidden shadow-xl border border-gray-200 max-w-md w-full">
              <img
                :src="selectedProject.image"
                :alt="selectedProject.name"
                class="w-full h-auto object-contain max-h-64 transition-opacity duration-300"
                loading="lazy"
              />
              <div class="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent pointer-events-none"></div>
            </div>
          </div>

          <!-- Titre et métadonnées avec fond élégant -->
          <div class="mb-8">
            <div class="bg-white rounded-xl p-6 shadow-lg border border-gray-100 mb-6">
              <h1 class="font-trebuchet-pixel text-3xl md:text-4xl font-bold mb-4 text-gray-900">{{ selectedProject.name }}</h1>

              <!-- Badges métadonnées -->
              <div class="flex items-center gap-3 flex-wrap mb-4">
                <span class="inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold border border-blue-200">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </svg>
                  {{ selectedProject.type }}
                </span>
                <span class="inline-flex items-center gap-1.5 px-4 py-1.5 bg-gray-50 text-gray-700 rounded-full text-xs font-medium border border-gray-200">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <path d="M16 2v4M8 2v4M3 10h18" />
                  </svg>
                  {{ selectedProject.year }}
                </span>
                <span
                  class="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold border"
                  :class="
                    selectedProject.status === 'Terminé' || selectedProject.status === 'Completed'
                      ? 'bg-green-50 text-green-700 border-green-200'
                      : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                  "
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {{ selectedProject.status }}
                </span>
              </div>

              <!-- Description -->
              <p class="text-sm md:text-base text-gray-700 leading-relaxed">{{ selectedProject.description }}</p>
            </div>
          </div>

          <!-- Technologies avec design raffiné -->
          <div v-if="selectedProject.technologies && selectedProject.technologies.length > 0" class="mb-8">
            <div class="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <div class="flex items-center gap-3 mb-4">
                <div class="p-2 bg-blue-100 rounded-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    class="text-blue-600"
                  >
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                  </svg>
                </div>
                <h2 class="font-trebuchet-pixel font-semibold text-lg text-gray-900">{{ $t('windows.otherProjects.technologies') }}</h2>
              </div>
              <div class="flex flex-wrap gap-2.5">
                <span
                  v-for="tech in selectedProject.technologies"
                  :key="tech"
                  class="inline-flex items-center px-4 py-2 bg-gradient-to-br from-gray-50 to-gray-100 text-gray-800 rounded-lg text-xs font-medium border border-gray-200 hover:border-blue-300 hover:bg-gradient-to-br hover:from-blue-50 hover:to-blue-100 hover:text-blue-700 transition-all duration-200 shadow-sm"
                >
                  {{ tech }}
                </span>
              </div>
            </div>
          </div>

          <!-- Grille de contenu : Contexte, Objectifs, Tâches -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <!-- Contexte -->
            <div v-if="selectedProject.context" class="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <div class="flex items-center gap-3 mb-4">
                <div class="p-2 bg-purple-100 rounded-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    class="text-purple-600"
                  >
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" />
                  </svg>
                </div>
                <h2 class="font-trebuchet-pixel font-semibold text-lg text-gray-900">{{ $t('common.context') }}</h2>
              </div>
              <p class="text-xs md:text-sm text-gray-700 leading-relaxed">{{ selectedProject.context }}</p>
            </div>

            <!-- Objectifs -->
            <div v-if="selectedProject.objectives && selectedProject.objectives.length > 0" class="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <div class="flex items-center gap-3 mb-4">
                <div class="p-2 bg-green-100 rounded-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    class="text-green-600"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <path d="M22 4L12 14.01l-3-3" />
                  </svg>
                </div>
                <h2 class="font-trebuchet-pixel font-semibold text-lg text-gray-900">{{ $t('common.objectives') }}</h2>
              </div>
              <ul class="space-y-2.5">
                <li v-for="objective in selectedProject.objectives" :key="objective" class="flex items-start gap-2.5 text-xs md:text-sm text-gray-700">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    class="text-green-500 mt-0.5 flex-shrink-0"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <path d="M22 4L12 14.01l-3-3" />
                  </svg>
                  <span class="leading-relaxed">{{ objective }}</span>
                </li>
              </ul>
            </div>
          </div>

          <!-- Tâches réalisées -->
          <div v-if="selectedProject.tasks && selectedProject.tasks.length > 0" class="mb-8">
            <div class="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <div class="flex items-center gap-3 mb-4">
                <div class="p-2 bg-orange-100 rounded-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    class="text-orange-600"
                  >
                    <path d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                  </svg>
                </div>
                <h2 class="font-trebuchet-pixel font-semibold text-lg text-gray-900">{{ $t('common.tasksDone') }}</h2>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div
                  v-for="task in selectedProject.tasks"
                  :key="task"
                  class="flex items-start gap-2.5 p-3 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200 hover:border-orange-300 hover:bg-gradient-to-br hover:from-orange-50 hover:to-orange-100 transition-all duration-200"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    class="text-orange-500 mt-0.5 flex-shrink-0"
                  >
                    <polyline points="9 11 12 14 22 4" />
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                  </svg>
                  <span class="text-xs md:text-sm text-gray-700 leading-relaxed flex-1">{{ task }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Liens avec design élégant -->
          <div v-if="selectedProject.websiteUrl || selectedProject.repositoryUrl" class="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
            <h2 class="font-trebuchet-pixel font-semibold text-lg text-gray-900 mb-4">{{ $t('common.links') }}</h2>
            <div class="flex flex-wrap gap-4">
              <a
                v-if="selectedProject.websiteUrl"
                :href="selectedProject.websiteUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="group flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg cursor-pointer transform hover:-translate-y-0.5"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" />
                </svg>
                <span class="text-sm font-trebuchet-pixel font-medium">{{ $t('buttons.linkWebSite') }}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  class="group-hover:translate-x-1 transition-transform duration-200"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
              <a
                v-if="selectedProject.repositoryUrl"
                :href="selectedProject.repositoryUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="group flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg hover:from-gray-900 hover:to-black transition-all duration-200 shadow-md hover:shadow-lg cursor-pointer transform hover:-translate-y-0.5"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"
                  />
                </svg>
                <span class="text-sm font-trebuchet-pixel font-medium">{{ $t('buttons.linkRepository') }}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  class="group-hover:translate-x-1 transition-transform duration-200"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Vue liste des projets -->
      <div v-else class="p-4 md:p-6">
        <div class="mb-6">
          <h2 class="font-trebuchet-pixel text-xl font-bold mb-2">{{ $t('windows.otherProjects.title') }}</h2>
          <p class="text-xs text-gray-600">{{ $t('windows.otherProjects.description') }}</p>
        </div>

        <!-- Grille de projets -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="project in projects"
            :key="project.id"
            @click="selectProject(project)"
            class="group cursor-pointer border border-gray-200 rounded-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:border-blue-400 bg-white"
          >
            <!-- Image du projet -->
            <div v-if="project.image" class="w-full h-40 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
              <img :src="project.image" :alt="project.name" class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
            </div>
            <div v-else class="w-full h-40 bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                class="text-gray-400"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
              </svg>
            </div>

            <!-- Contenu de la carte -->
            <div class="p-4">
              <div class="flex items-start justify-between mb-2">
                <h3 class="font-trebuchet-pixel font-semibold text-sm text-gray-900 group-hover:text-blue-600 transition-colors flex-1">
                  {{ project.name }}
                </h3>
              </div>

              <p class="text-xs text-gray-600 mb-3 line-clamp-2">{{ project.shortDescription }}</p>

              <!-- Badges -->
              <div class="flex items-center gap-2 mb-3 flex-wrap">
                <span class="text-xxs bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-200">
                  {{ project.type }}
                </span>
                <span class="text-xxs text-gray-500">{{ project.year }}</span>
              </div>

              <!-- Technologies (aperçu) -->
              <div v-if="project.technologies && project.technologies.length > 0" class="flex flex-wrap gap-1 mb-3">
                <span v-for="tech in project.technologies.slice(0, 3)" :key="tech" class="text-xxs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                  {{ tech }}
                </span>
                <span v-if="project.technologies.length > 3" class="text-xxs text-gray-500 px-2 py-0.5"> +{{ project.technologies.length - 3 }} </span>
              </div>

              <!-- Indicateur de clic -->
              <div class="flex items-center gap-1 text-xxs text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity mt-2">
                <span>{{ $t('windows.otherProjects.clickToView') }}</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
